import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import { Horse } from '@phosphor-icons/react';
import api from '../../services/api';
import { useTranslation } from 'react-i18next';
import {
  SMART_COLORS as COLORS,
  smartActionButtonSx,
  smartCardSx as cardSx,
  smartTableContainerSx,
  smartTableHeadSx,
  smartTableSx,
} from '../../theme/smartStableTheme';
import SmartTablePagination, { useSmartPagination } from '../../components/SmartTablePagination';
import { todayDateInput } from '../../utils/dateUtils';

const apiUrl = process.env.REACT_APP_API_URL;

const isInactive = (row) => Number(row?.is_active ?? 1) !== 1;
const isActive = (row) => !isInactive(row);

function normalizeRows(arr) {
  return (Array.isArray(arr) ? arr : []).map((horse) => ({
    id: horse.id ?? horse.horse_id,
    name:
      horse.name ||
      horse.horse_name ||
      horse.horse_name_ar ||
      horse.horse_name_en ||
      `#${horse.id ?? horse.horse_id}`,
    owner: horse.owner_name || '',
    breed: horse.breed_arabic || horse.breed_english || horse.breed_name || '',
    color: horse.color_arabic || horse.color_english || horse.color_name || '',
    disabledAt: horse.modified_at || horse.deleted_at || horse.deletedDate || horse.deleted_on || '',
    is_active: Number(horse.is_active ?? 1),
    is_deleted: Number(horse.is_deleted ?? 0),
    raw: horse,
  }));
}

function MetricCard({ icon, title, value, helper, color = COLORS.gold, soft = COLORS.goldSoft }) {
  return (
    <Paper elevation={0} sx={{ ...cardSx, p: 2, minHeight: 104 }}>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Avatar sx={{ bgcolor: soft, color, width: 48, height: 48 }}>{icon}</Avatar>
        <Box>
          <Typography sx={{ color, fontWeight: 800 }}>{title}</Typography>
          <Typography variant="h5" sx={{ color: COLORS.text, fontWeight: 900 }}>{value}</Typography>
          {helper && <Typography variant="caption" sx={{ color: COLORS.muted }}>{helper}</Typography>}
        </Box>
      </Stack>
    </Paper>
  );
}

export default function RestoreDeletedHorses() {
  const { t, i18n } = useTranslation();
  const isRtl = String(i18n.language || '').startsWith('ar');

  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('inactive');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const abortRef = useRef(null);
  const [selected, setSelected] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, msg: '', sev: 'success' });

  const showSnack = useCallback((msg, sev = 'success') => setSnackbar({ open: true, msg, sev }), []);

  const allChecked = rows.length > 0 && selected.length === rows.length;
  const someChecked = selected.length > 0 && selected.length < rows.length;

  const canActivateSelected = useMemo(
    () => selected.some((id) => isInactive(rows.find((row) => row.id === id))),
    [rows, selected]
  );
  const canDeactivateSelected = useMemo(
    () => selected.some((id) => isActive(rows.find((row) => row.id === id))),
    [rows, selected]
  );

  const {
    page,
    rowsPerPage,
    paginatedItems: pagedRows,
    handleChangePage,
    handleChangeRowsPerPage,
  } = useSmartPagination(rows, [q, filter]);

  const summary = useMemo(
    () => ({
      active: rows.filter(isActive).length,
      inactive: rows.filter(isInactive).length,
      total: rows.length,
    }),
    [rows]
  );

  const loadList = useCallback(
    async (is_active) => {
      const response = await api.get(`${apiUrl}/horseslist`, {
        params: { is_deleted: 0, is_active, q: q || undefined },
        signal: abortRef.current?.signal,
      });
      return normalizeRows(response.data);
    },
    [q]
  );

  const loadRows = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      abortRef.current?.abort?.();
      abortRef.current = new AbortController();

      let data = [];
      if (filter === 'inactive') data = await loadList(0);
      else if (filter === 'active') data = await loadList(1);
      else {
        const [activeRows, inactiveRows] = await Promise.all([loadList(1), loadList(0)]);
        data = [...activeRows, ...inactiveRows].sort((a, b) => Number(b.id || 0) - Number(a.id || 0));
      }

      setRows(data);
      setSelected([]);
    } catch (error) {
      if (!(error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError')) {
        setErr(error?.response?.data?.message || error?.response?.data?.error || t('restoreDeletedHorses.loadError'));
      }
    } finally {
      setLoading(false);
    }
  }, [filter, loadList, t]);

  useEffect(() => {
    loadRows();
    return () => abortRef.current?.abort?.();
  }, [loadRows]);

  const activateOne = async (id) => {
    try {
      await api.put(`${apiUrl}/horses/${id}`, { is_active: 1 });
      return true;
    } catch {
      return false;
    }
  };

  const deactivateOne = async (id) => {
    try {
      await api.put(`${apiUrl}/horses/${id}`, { is_active: 0 });
      return true;
    } catch {
      return false;
    }
  };

  const handleActivate = async (ids) => {
    if (!ids?.length) return;
    const results = await Promise.allSettled(ids.map(activateOne));
    const ok = results.filter((result) => result.status === 'fulfilled' && result.value).length;
    const fail = ids.length - ok;
    if (ok) showSnack(t('restoreDeletedHorses.activated'), 'success');
    if (fail) showSnack(t('restoreDeletedHorses.activateFailed'), 'warning');
    await loadRows();
  };

  const handleDeactivate = async (ids) => {
    if (!ids?.length) return;
    const results = await Promise.allSettled(ids.map(deactivateOne));
    const ok = results.filter((result) => result.status === 'fulfilled' && result.value).length;
    const fail = ids.length - ok;
    if (ok) showSnack(t('restoreDeletedHorses.deactivated'), 'success');
    if (fail) showSnack(t('restoreDeletedHorses.deactivateFailed'), 'warning');
    await loadRows();
  };

  const handleExportJSON = () => {
    try {
      const blob = new Blob([JSON.stringify(rows, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `horses_activation_${filter}_${todayDateInput()}.json`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      showSnack(t('restoreDeletedHorses.exported'));
    } catch {
      showSnack(t('restoreDeletedHorses.exportFailed'), 'error');
    }
  };

  const handleSelectAll = (checked) => {
    setSelected(checked ? rows.map((row) => row.id) : []);
  };

  const handleSelectRow = (id, checked) => {
    setSelected((prev) => (checked ? [...prev, id] : prev.filter((item) => item !== id)));
  };

  return (
    <Box
      className="smart-page-container"
      dir={isRtl ? 'rtl' : 'ltr'}
      sx={{ minHeight: '100vh', bgcolor: COLORS.page, maxWidth: 'var(--app-page-max)', mx: 'auto' }}
    >
      <Paper elevation={0} sx={{ ...cardSx, p: { xs: 2, md: 3 }, mb: 3 }}>
        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'stretch', lg: 'center' }}>
          <Box>
            <Typography variant="h4" sx={{ color: COLORS.text, fontWeight: 900 }}>{t('restoreDeletedHorses.title')}</Typography>
            <Typography sx={{ color: COLORS.muted, mt: 0.5, maxWidth: 820 }}>{t('restoreDeletedHorses.subtitle')}</Typography>
          </Box>
          <Stack direction="row" spacing={1} justifyContent={{ xs: 'flex-start', lg: 'flex-end' }} flexWrap="wrap" useFlexGap>
            <Tooltip title={t('refresh')}>
              <span>
                <IconButton onClick={loadRows} disabled={loading} sx={{ border: `1px solid ${COLORS.line}`, color: COLORS.gold }}>
                  <RefreshIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={t('restoreDeletedHorses.export')}>
              <span>
                <IconButton onClick={handleExportJSON} disabled={!rows.length} sx={{ border: `1px solid ${COLORS.line}`, color: COLORS.text }}>
                  <DownloadIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Stack>
      </Paper>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <MetricCard icon={<Horse size={26} weight="fill" />} title={t('restoreDeletedHorses.total')} value={summary.total} />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <MetricCard icon={<ToggleOnIcon />} title={t('restoreDeletedHorses.active')} value={summary.active} color={COLORS.green} soft={COLORS.greenSoft} />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <MetricCard icon={<ToggleOffIcon />} title={t('restoreDeletedHorses.inactive')} value={summary.inactive} color={COLORS.rose} soft={COLORS.roseSoft} />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <MetricCard icon={<Checkbox checked={Boolean(selected.length)} disabled />} title={t('restoreDeletedHorses.selected')} value={selected.length} color={COLORS.blue} soft={COLORS.blueSoft} />
        </Grid>
      </Grid>

      <Paper elevation={0} sx={{ ...cardSx, p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }}>
          <TextField
            fullWidth
            size="small"
            placeholder={t('restoreDeletedHorses.search')}
            value={q}
            onChange={(event) => setQ(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: { xs: '100%', md: 220 } }}>
            <InputLabel>{t('restoreDeletedHorses.status')}</InputLabel>
            <Select label={t('restoreDeletedHorses.status')} value={filter} onChange={(event) => setFilter(event.target.value)}>
              <MenuItem value="inactive">{t('restoreDeletedHorses.inactiveOnly')}</MenuItem>
              <MenuItem value="active">{t('restoreDeletedHorses.activeOnly')}</MenuItem>
              <MenuItem value="both">{t('all')}</MenuItem>
            </Select>
          </FormControl>
          <Button variant="contained" onClick={loadRows} sx={{ bgcolor: COLORS.gold, '&:hover': { bgcolor: COLORS.goldHover }, minWidth: 110 }}>
            {t('restoreDeletedHorses.apply')}
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          <Button
            startIcon={<PlayCircleOutlineIcon />}
            disabled={!selected.length || !canActivateSelected}
            onClick={() => handleActivate(selected)}
            sx={{ color: COLORS.green }}
          >
            {t('restoreDeletedHorses.activateSelected')}
          </Button>
          <Button
            startIcon={<PauseCircleOutlineIcon />}
            disabled={!selected.length || !canDeactivateSelected}
            onClick={() => handleDeactivate(selected)}
            sx={{ color: COLORS.rose }}
          >
            {t('restoreDeletedHorses.deactivateSelected')}
          </Button>
        </Stack>
      </Paper>

      {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}

      {loading ? (
        <Stack spacing={1}>
          {Array.from({ length: 8 }).map((_, index) => (
            <Skeleton key={index} height={68} variant="rounded" />
          ))}
        </Stack>
      ) : rows.length === 0 ? (
        <Alert severity="info">{t('restoreDeletedHorses.noData')}</Alert>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ ...cardSx, ...smartTableContainerSx, overflow: 'hidden' }}>
          <Table size="small" sx={smartTableSx}>
            <TableHead sx={smartTableHeadSx}>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox checked={allChecked} indeterminate={someChecked} onChange={(event) => handleSelectAll(event.target.checked)} />
                </TableCell>
                <TableCell>#</TableCell>
                <TableCell>{t('horse.name')}</TableCell>
                <TableCell>{t('owner')}</TableCell>
                <TableCell>{t('breed')}</TableCell>
                <TableCell>{t('color')}</TableCell>
                <TableCell>{t('restoreDeletedHorses.status')}</TableCell>
                <TableCell>{t('restoreDeletedHorses.disabledAt')}</TableCell>
                <TableCell align="center" sx={{ width: 120 }}>{t('actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pagedRows.map((row, index) => {
                const inactive = isInactive(row);
                return (
                  <TableRow key={row.id} hover sx={{ '& .MuiTableCell-root': { borderBottom: `1px solid ${COLORS.line}` } }}>
                    <TableCell padding="checkbox">
                      <Checkbox checked={selected.includes(row.id)} onChange={(event) => handleSelectRow(row.id, event.target.checked)} />
                    </TableCell>
                    <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1.2} alignItems="center">
                        <Avatar sx={{ bgcolor: inactive ? COLORS.roseSoft : COLORS.goldSoft, color: inactive ? COLORS.rose : COLORS.gold, width: 38, height: 38 }}>
                          <Horse size={22} weight="fill" />
                        </Avatar>
                        <Box>
                          <Typography sx={{ color: COLORS.text, fontWeight: 900 }}>{row.name}</Typography>
                          <Typography variant="caption" sx={{ color: COLORS.muted }}>{t('horse_id')} {row.id}</Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>{row.owner || '-'}</TableCell>
                    <TableCell>{row.breed || '-'}</TableCell>
                    <TableCell>{row.color || '-'}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        icon={inactive ? <ToggleOffIcon /> : <ToggleOnIcon />}
                        label={inactive ? t('restoreDeletedHorses.inactive') : t('restoreDeletedHorses.active')}
                        sx={{
                          bgcolor: inactive ? COLORS.roseSoft : COLORS.greenSoft,
                          color: inactive ? COLORS.rose : COLORS.green,
                          fontWeight: 900,
                        }}
                      />
                    </TableCell>
                    <TableCell>{inactive ? row.disabledAt || '-' : '-'}</TableCell>
                    <TableCell align="center">
                      {inactive ? (
                        <Tooltip title={t('restoreDeletedHorses.activate')}>
                          <IconButton onClick={() => handleActivate([row.id])} sx={smartActionButtonSx(COLORS.green, COLORS.greenSoft)}>
                            <PlayCircleOutlineIcon />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title={t('restoreDeletedHorses.deactivate')}>
                          <IconButton onClick={() => handleDeactivate([row.id])} sx={smartActionButtonSx(COLORS.rose, COLORS.roseSoft)}>
                            <PauseCircleOutlineIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <SmartTablePagination
            count={rows.length}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>
      )}

      <Snackbar
        open={snackbar.open}
        onClose={() => setSnackbar((state) => ({ ...state, open: false }))}
        autoHideDuration={snackbar.sev === 'error' ? 5000 : 2500}
      >
        <Alert severity={snackbar.sev} onClose={() => setSnackbar((state) => ({ ...state, open: false }))}>
          {snackbar.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
